import { INameId } from 'constants/globalTypes'
import { IMealSupplierModalState } from 'pages/Food/MealSuppliersModal'
import { IUnit } from 'types/fields.interface'

export interface ICreateMealTypeRequest {
  name: string
  serviceWindowStart: string
  serviceWindowEnd: string
  planningCutOffTime: string
}

export interface ICreateMealSupplierRequest
  extends Omit<IMealSupplierModalState, 'dietaryPreferenceTypes'> {
  dietaryPreferenceTypes: number[]
}

export interface IMealSupplierSingleResponse extends ICreateMealSupplierRequest {
  id: number
}

export interface ICreateIngredientRequest {
  name: string
  unitId: number
}

export interface IIngredientSingleResponse {
  id: number
  name: string
  unit: IUnit
}

export interface IGetAllFoodEligibleEmployeesSingleResponse {
  id: number
  firstName: string
  lastName: string
  foodSettings: {
    id: number
    provisioningMode: number
    foodSupplier: {
      id: number
      name: string
      dietaryPreferenceTypes: number[]
      isConnectedWithFoodInventory: true
    }
    maxMealsPerOperationalDay: number
    selectedMealTypes: [
      {
        id: number
        mealType: {
          id: number
          name: string
          serviceWindowEnd: string
          planningCutOffTime: string
        }
        createdAt: string
      },
    ]
    createdAt: string
  }
}

export interface ICreateDishRequest {
  name: string
  mealTypeId: number
  dietaryPreferenceTypes: number[]
  supplierId: number
  ingredients: { ingredientId: number; amount: number }[]
}

export interface IGetAllDishesSingleResponse {
  id: number
  name: string
  mealType: INameId
  supplier: {
    id: number
    name: string
    dietaryPreferenceTypes: number[]
    isConnectedWithFoodInventory: boolean
  }
  dietaryPreferenceTypes: number[]
}

export interface IGetAllMealTypesSingleResponse {
  id: number
  name: string
  serviceWindowStart: string
  serviceWindowEnd: string
  planningCutOffTime: string
}

export interface IGetSelectedDishesReponse {
  dishes: {
    id: number
    dayInWeek: number
    mealType: INameId
    dish: {
      id: number
      name: string
      mealType: INameId
      supplier: {
        id: number
        name: string
        dietaryPreferenceTypes: number[]
        isConnectedWithFoodInventory: boolean
      }
      dietaryPreferenceTypes: number[]
    }
    supplier: {
      id: number
      name: string
      dietaryPreferenceTypes: number[]
      isConnectedWithFoodInventory: boolean
    }
    dietaryPreferenceType: number
    createdAt: string
  }[]
}

export interface IPostSelectedDishRequest {
  selectedFoodDishes: {
    dayInWeek: number
    dietaryPreferenceType: number
    mealTypeId: number
    dishId: number
    supplierId: number
  }[]
}

export interface IDishSingleResponse extends IGetAllDishesSingleResponse {
  ingredients: {
    id: number
    ingredient: {
      id: number
      name: string
      unit: IUnit
    }
    amount: number
  }[]
}

export interface IFoodCategorySingleResponse {
  id: number
  name: string
  imageUrl: string
  createdAt: string
}

export interface IGetFoodCompanySettingsResponse {
  id: number
  maxMealsPerOperationalDay: number
  mealTypePriorities: {
    id: number
    mealType: {
      id: number
      name: string
      serviceWindowStart: string
      serviceWindowEnd: string
      planningCutOffTime: string
    }
    priority: number
    createdAt: string
  }[]

  createdAt: string
}

export interface IGetAllFoodProvisioningRecordsSingleResponse {
  foodSupplier: {
    id: number
    name: string
    dietaryPreferenceTypes: number[]
    isConnectedWithFoodInventory: boolean
  }
  mealTypes: {
    mealType: {
      id: number
      name: string
      serviceWindowStart: string
      serviceWindowEnd: string
      planningCutOffTime: string
    }
    groups: {
      dietaryPreferenceType: number
      provisioningMode: number
      count: number
    }[]

    totalCount: number
  }[]
}

export interface IAddCompanyFoodSettingsRequest {
  maxMealsPerOperationalDay: number
  mealTypePriorities: {
    mealTypeId: number
    priority: number
  }[]
}

export interface IAddFoodEmployeeSettingsRequest {
  provisioningMode: number
  foodSupplierId: number
  maxMealsPerOperationalDay: number
  selectedMealTypeIds: number[]
}

export interface IAddAdHocFoodProvisioningRequest {
  date: string
  mealTypes: {
    mealTypeId: number
    count: number
    dietaryPreferenceType: number
    foodSupplierId: number
    provisioningMode: number
  }[]
}

export interface IAddStandByProvisioningRequest {
  date: string
  mealTypes: {
    mealTypeId: number
    count: number
    dietaryPreferenceType: number
    foodSupplierId: number
  }[]
}

export interface IFoodInventoyItemSingleResponse {
  id: number
  name: string
  category: INameId
  ingredient: {
    id: number
    name: string
    unit: IUnit
  }
  initialAmount: number
  remainingAmount: number
  price: number
}

export interface ICreateUpdateFoodInventoryItemRequest {
  name: string
  categoryId: number
  ingredientId: number
  amount: number
  price: number
}

export interface IGetFoodReportResponse {
  report: IFoodReport
}

export interface IFoodReport {
  id: number
  date: string
  isConfirmed: boolean
  meals: {
    id: number
    shift: {
      id: number
      companyId: number
      name: string
      shiftStart: number
      minutes: number
      info: string
      color: string
      absence: boolean
      absenceType: number
    }
    supplier: INameId
    mealType: INameId
    dietaryPreferenceType: number
    dish: INameId
    count: number
  }[]

  guestMeals: {
    id: number
    supplier: INameId
    mealType: INameId
    dish: INameId
    count: number
  }[]
}

export interface IGenerateFoodReportRequest {
  date: string
  guestMeals: {
    dietaryPreferenceType: number
    supplierId: number
    mealTypeIds: number[]
    count: number
  }[]
}

export interface IEditFoodReport {
  meals: {
    id: number
    count: number
  }[]

  guestMeals: {
    id?: number
    mealSupplierId: number
    mealTypeIds: number[]
    dietaryPreferenceType: number
    count: number
  }[]
}

export enum FoodKitchenOperatorType {
  Internal = 1,
  Contractor = 2,
}

export interface IFoodDietaryTagResponse {
  id: number
  code: string
  name: string
  active: boolean
  createdAt: string
}

export interface ICreateFoodDietaryTagRequest {
  code: string
  name: string
}

export interface IUpdateFoodDietaryTagRequest {
  name: string
}

export interface IFoodKitchenResponse {
  id: number
  name: string
  operatorType: number
  packoutSupport: boolean
  softCapacity: number
  standbyEnabled: boolean
  dietaryTagCapabilities: IFoodDietaryTagResponse[]
  active: boolean
  createdAt: string
}

export interface ICreateFoodKitchenRequest {
  name: string
  operatorType: number
  packoutSupport: boolean
  softCapacity: number
  standbyEnabled: boolean
  dietaryTagCodes: string[]
}

export interface IUpdateFoodKitchenRequest {
  name?: string
  operatorType?: number
  packoutSupport?: boolean
  softCapacity?: number
  standbyEnabled?: boolean
  dietaryTagCodes?: string[]
}

export interface IFoodMealWindowDowVariantResponse {
  id: number
  daysInWeek: number[]
  serviceStart: string | null
  serviceEnd: string | null
  cutoffTime: string | null
  cutoffDayOffset: number | null
  defaultKitchenId: number | null
}

export interface IFoodMealWindowResponse {
  id: number
  name: string
  serviceStart: string
  serviceEnd: string
  cutoffTime: string
  cutoffDayOffset: number
  defaultKitchenId: number | null
  createdAt: string
  dowVariants: IFoodMealWindowDowVariantResponse[]
}

export interface ICreateFoodMealWindowDowVariantRequest {
  daysInWeek: number[]
  serviceStart?: string | null
  serviceEnd?: string | null
  cutoffTime?: string | null
  cutoffDayOffset?: number | null
  defaultKitchenId?: number | null
}

export interface ICreateFoodMealWindowRequest {
  name: string
  serviceStart: string
  serviceEnd: string
  cutoffTime: string
  cutoffDayOffset: number
  defaultKitchenId?: number | null
  dowVariants?: ICreateFoodMealWindowDowVariantRequest[]
}

export interface IUpdateFoodMealWindowRequest {
  name?: string
  serviceStart?: string
  serviceEnd?: string
  cutoffTime?: string
  cutoffDayOffset?: number
  defaultKitchenId?: number | null
  dowVariants?: ICreateFoodMealWindowDowVariantRequest[]
}

export interface IFoodCatchmentRuleResponse {
  id: number
  kitchenId: number
  locationNodeId: number | null
  departmentId: number | null
  isFallback: boolean
  createdAt: string
}

export interface ICreateFoodCatchmentRuleRequest {
  kitchenId: number
  locationNodeId?: number | null
  departmentId?: number | null
  isFallback?: boolean
}

export interface IUpdateFoodCatchmentRuleRequest {
  kitchenId?: number
  locationNodeId?: number | null
  departmentId?: number | null
  isFallback?: boolean
}

export const FoodProvisioningMode = {
  MessOrHall: 1,
  Packout: 2,
  OptOut: 3,
}

export const FoodConfigUnknownPresencePolicy = {
  NoMeal: 1,
  PlanMeal: 2,
  StandbyReserve: 3,
}

export interface IFoodWindowModeDefault {
  id: number
  windowDefId: number
  windowDefName: string
  mode: number
}

export interface IEmployeeFoodProfile {
  id: number
  userId: number
  stickyKitchenId: number | null
  windowModeDefaults: IFoodWindowModeDefault[]
  createdAt: string
  updatedAt: string
}

export interface IUpsertEmployeeFoodProfileRequest {
  stickyKitchenId?: number | null
  windowModeDefaults: {
    windowDefId: number
    mode: number
  }[]
}

export interface IFoodAttendanceReconciliation {
  noShowGraceMinutes: number
  cancelIntentOnNoShow: boolean
  applyShowRateCoefficient: boolean
}

export interface IFoodSitePolicy {
  maxMealsPerDay: number
  mealSelectionPriorityWindowIds: number[]
  unknownPresencePolicy: number
  defaultMode: number
  attendanceReconciliation: IFoodAttendanceReconciliation
}

export interface IUpsertFoodSitePolicyRequest {
  maxMealsPerDay: number
  mealSelectionPriorityWindowIds: number[]
  unknownPresencePolicy: number
  defaultMode: number
  attendanceReconciliation: IFoodAttendanceReconciliation
}

export interface IFoodConfigVersionPayload {
  policy: IFoodSitePolicy
  mealWindows: any[]
  kitchens: any[]
  catchmentRules: any[]
  standbyConfigs: any[]
}

export interface IFoodConfigVersion {
  id: number
  effectiveFromOperationalDay: string
  payload: IFoodConfigVersionPayload
  createdBy: number
  createdAt: string
}

export interface IGenerateFoodIntentsRequest {
  operationalDay: string
  windowDefId?: number
}

export interface IGenerateFoodIntentsResponse {
  message: string
}

export interface IFoodPreviewDietaryTagCount {
  dietaryTag: IFoodDietaryTagResponse
  count: number
}

export interface INextDayPreviewRow {
  kitchen: IFoodKitchenResponse
  windowDef: IFoodMealWindowResponse
  mode: number
  totalCount: number
  dietaryTags: IFoodPreviewDietaryTagCount[]
}
